import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { ViewWillEnter } from '@ionic/angular';
import { BackButtonService } from 'src/app/services/back-button.service';
import { RestService } from 'src/app/services/rest.service';
import { StorageService } from 'src/app/services/storage.service';
import { Subject, takeUntil } from 'rxjs';
import { CameraResultType, ImageOptions, Photo } from '@capacitor/camera';
import { HttpEventType } from '@angular/common/http';
import { PhotoService } from 'src/app/services/photo.service';
import { AttachmentsService } from 'src/app/services/attachments.service';
import { SnackbarService } from '@bcl/design-system';
import { TranslateService } from '@ngx-translate/core';
import {
  WasteSource,
  SieveSize,
  WasteRegistrationData,
  WastePhoto,
  WASTE_SOURCE_OPTIONS,
  SIEVE_SIZE_OPTIONS,
} from './waste-registration.model';

const SAVED_SIEVE_SIZE_KEY = 'waste_registration_saved_sieve_size';

@Component({
  selector: 'app-waste-registration',
  templateUrl: './waste-registration.component.html',
  styleUrls: ['./waste-registration.component.scss'],
})
export class WasteRegistrationComponent
  implements OnInit, OnDestroy, ViewWillEnter
{
  public orderId: number;
  public processingLine: string | null = null;
  public form: FormGroup;
  public isSourceSectionOpen = true;
  public isDetailsSectionOpen = true;
  public isSaving = false;

  public readonly wasteSourceOptions = WASTE_SOURCE_OPTIONS;
  public readonly sieveSizeOptions = SIEVE_SIZE_OPTIONS;

  // Separate controls for checkboxes (radio-like behavior)
  public sourceControls: { [key: string]: FormControl } = {};
  public sizeControls: { [key: string]: FormControl } = {};

  // Photo handling
  public photos: WastePhoto[] = [];
  public photosAddedList: WastePhoto[] = [];
  public photosDeletedList: WastePhoto[] = [];
  public maxPhotos = 5;
  public uploading = false;
  public currentUploadProgress = 0;
  public maxFileSize = 3 * 1024 * 1024; // 3 MB

  // Gallery/Lightbox
  public isGalleryOpen = false;
  public currentGalleryIndex = 0;

  // Weight counter
  public otherKgValue: number | string = 0;
  private readonly otherKgStep = 0.1;

  private destroyed$ = new Subject<void>();
  private isUpdatingSource = false;
  private isUpdatingSize = false;
  private savedSieveSize: SieveSize = '1.18mm';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private backButtonService: BackButtonService,
    private cameraService: PhotoService,
    private attachmentsService: AttachmentsService,
    private snackbarService: SnackbarService,
    private restService: RestService,
    private storageService: StorageService,
    private translate: TranslateService
  ) {
    this.orderId = +this.route.snapshot.paramMap.get('id');
    this.processingLine = this.route.snapshot.queryParamMap.get('line');

    // Create source controls
    this.wasteSourceOptions.forEach((option, index) => {
      this.sourceControls[option.id] = new FormControl(index === 0);
    });

    // Create size controls
    this.sieveSizeOptions.forEach((size, index) => {
      this.sizeControls[size.id] = new FormControl(index === 0);
    });

    this.form = this.createForm();
  }

  public get selectedSource(): WasteSource {
    return this.form.get('source')?.value;
  }

  public get selectedSieveSize(): SieveSize | null {
    return this.form.get('sieveSize')?.value;
  }

  public get currentGalleryPhoto(): WastePhoto | null {
    return this.photos[this.currentGalleryIndex] || null;
  }

  public get isOtherKgAtMin(): boolean {
    const numValue = parseFloat(String(this.otherKgValue).replace(',', '.'));
    return isNaN(numValue) || numValue <= 0;
  }

  ngOnInit(): void {
    // Subscribe to source control changes
    this.wasteSourceOptions.forEach((option) => {
      this.sourceControls[option.id].valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((checked) => {
          if (!this.isUpdatingSource) {
            this.handleSourceChange(option.id as WasteSource, checked);
          }
        });
    });

    // Subscribe to size control changes
    this.sieveSizeOptions.forEach((size) => {
      this.sizeControls[size.id].valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((checked) => {
          if (!this.isUpdatingSize) {
            this.handleSizeChange(size.id, size.value, checked);
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  async ionViewWillEnter(): Promise<void> {
    this.setupBackButton();
    await this.loadSavedSieveSize();
    this.resetForm();
  }

  public toggleSourceSection(): void {
    this.isSourceSectionOpen = !this.isSourceSectionOpen;
  }

  public toggleDetailsSection(): void {
    this.isDetailsSectionOpen = !this.isDetailsSectionOpen;
  }

  public getSelectedSourceLabel(): string {
    const source = this.wasteSourceOptions.find(
      (o) => o.id === this.selectedSource
    );
    return source?.labelKey || '';
  }

  public async submit(): Promise<void> {
    if (this.isSaving || this.uploading) {
      return;
    }

    const formValue = this.form.getRawValue();

    // Prepare data without photos - photos will be associated after getting waste ID
    const data: WasteRegistrationData = {
      source: formValue.source,
      sieveSize: formValue.source === 'Sieve' ? formValue.sieveSize : null,
      otherKg: formValue.otherKg,
      findings: formValue.findings,
      photos: [],
    };

    this.isSaving = true;

    try {
      const response: any = await this.restService.saveWasteRegistration(
        this.orderId,
        this.processingLine || '',
        data
      );

      const wasteId = response?.id;

      // If we have photos and got a waste ID, associate them with the waste registration
      if (wasteId && formValue.source === 'Other') {
        await this.associatePhotosWithWaste(wasteId);
      }

      // Save the sieve size to local storage for next time
      if (formValue.source === 'Sieve' && formValue.sieveSize) {
        await this.saveSieveSize(formValue.sieveSize);
      }

      this.snackbarService.open(
        'green',
        'WASTE_REGISTRATION.MESSAGES.SAVED_SUCCESS'
      );
      this.resetForm();
      this.goBack();
    } catch (error) {
      console.error('Error saving waste registration:', error);
      this.snackbarService.open(
        'red',
        'WASTE_REGISTRATION.MESSAGES.SAVED_ERROR'
      );
    } finally {
      this.isSaving = false;
    }
  }

  public async takePhoto(): Promise<void> {
    if (this.photos.length >= this.maxPhotos || this.uploading) {
      if (this.photos.length >= this.maxPhotos) {
        this.snackbarService.open(
          'red',
          'WASTE_REGISTRATION.MESSAGES.MAX_PHOTOS_REACHED'
        );
      }
      return;
    }

    try {
      const options: ImageOptions = {
        quality: 90,
        resultType: CameraResultType.DataUrl,
        correctOrientation: true,
        allowEditing: false,
        height: 800,
        width: 800,
        saveToGallery: true,
        promptLabelPhoto: this.translate.instant(
          'WASTE_REGISTRATION.LABELS.FROM_GALLERY'
        ),
        promptLabelPicture: this.translate.instant(
          'WASTE_REGISTRATION.LABELS.TAKE_PICTURE'
        ),
        promptLabelHeader: this.translate.instant(
          'WASTE_REGISTRATION.LABELS.PHOTO_HEADER'
        ),
      };

      const imageData: Photo = await this.cameraService.getPhoto(options);

      if (imageData.dataUrl) {
        const fileName = `photo_${Date.now()}.jpg`;
        const mimeType = imageData.format
          ? `image/${imageData.format}`
          : 'image/jpeg';
        const base64Length =
          imageData.dataUrl.length - (imageData.dataUrl.indexOf(',') + 1);
        const size = Math.round((base64Length * 3) / 4);

        // Check file size
        if (size > this.maxFileSize) {
          this.snackbarService.open(
            'red',
            'WASTE_REGISTRATION.MESSAGES.FILE_TOO_LARGE'
          );
          return;
        }

        // Create photo object with uploading state
        const newPhoto: WastePhoto = {
          dataUrl: imageData.dataUrl,
          fileName,
          size,
          mimeType,
          uploading: true,
          uploadProgress: 0,
        };

        this.photos.push(newPhoto);

        // Upload to server
        this.uploadPhoto(newPhoto);
      }
    } catch (error: any) {
      if (error.message !== 'User cancelled photos app') {
        this.snackbarService.open(
          'red',
          'WASTE_REGISTRATION.MESSAGES.ERROR_TAKING_PHOTO'
        );
      }
    }
  }

  public deletePhoto(index: number): void {
    const photo = this.photos[index];

    // Track deleted photos that were already uploaded to server
    if (photo.referenceId) {
      this.photosDeletedList.push(photo);
      // Remove from added list if it was just added
      this.photosAddedList = this.photosAddedList.filter(
        (p) => p.referenceId !== photo.referenceId
      );
    }

    this.photos.splice(index, 1);
  }

  // Gallery/Lightbox methods
  public openGallery(index: number): void {
    // Only open if photo is fully uploaded
    if (this.photos[index] && !this.photos[index].uploading) {
      this.currentGalleryIndex = index;
      this.isGalleryOpen = true;
    }
  }

  public closeGallery(): void {
    this.isGalleryOpen = false;
  }

  public nextPhoto(): void {
    if (this.currentGalleryIndex < this.photos.length - 1) {
      this.currentGalleryIndex++;
    }
  }

  public prevPhoto(): void {
    if (this.currentGalleryIndex > 0) {
      this.currentGalleryIndex--;
    }
  }

  public formatBytes(bytes: number): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  public onOtherKgInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.otherKgValue = input.value;
  }

  public onOtherKgBlur(): void {
    const numValue = parseFloat(String(this.otherKgValue).replace(',', '.'));
    if (!isNaN(numValue) && numValue >= 0) {
      this.otherKgValue = Math.round(numValue * 10) / 10;
    } else {
      this.otherKgValue = 0;
    }
    this.form.get('otherKg')?.setValue(this.otherKgValue, { emitEvent: false });
  }

  public incrementOtherKg(): void {
    const currentValue =
      parseFloat(String(this.otherKgValue).replace(',', '.')) || 0;
    this.otherKgValue = Math.round((currentValue + this.otherKgStep) * 10) / 10;
    this.form.get('otherKg')?.setValue(this.otherKgValue, { emitEvent: false });
  }

  public decrementOtherKg(): void {
    const currentValue =
      parseFloat(String(this.otherKgValue).replace(',', '.')) || 0;
    const newValue = currentValue - this.otherKgStep;
    this.otherKgValue = newValue >= 0 ? Math.round(newValue * 10) / 10 : 0;
    this.form.get('otherKg')?.setValue(this.otherKgValue, { emitEvent: false });
  }

  public goBack(): void {
    this.router.navigate(['processing-details', this.orderId], {
      queryParams: { line: this.processingLine },
    });
  }

  public resetForm(): void {
    // Clear photos and tracking lists
    this.photos = [];
    this.photosAddedList = [];
    this.photosDeletedList = [];
    this.isGalleryOpen = false;
    this.currentGalleryIndex = 0;
    this.otherKgValue = 0;

    this.form.reset({
      source: 'Sieve',
      sieveSize: this.savedSieveSize,
      otherKg: 0,
      findings: null,
    });

    // Reset source controls - select sieve by default
    this.isUpdatingSource = true;
    this.wasteSourceOptions.forEach((option, index) => {
      this.sourceControls[option.id].setValue(index === 0, {
        emitEvent: false,
      });
    });
    this.isUpdatingSource = false;

    this.isUpdatingSize = true;
    this.sieveSizeOptions.forEach((size) => {
      this.sizeControls[size.id].setValue(size.value === this.savedSieveSize, {
        emitEvent: false,
      });
    });
    this.isUpdatingSize = false;

    // Reset accordion sections
    this.isSourceSectionOpen = true;
    this.isDetailsSectionOpen = true;
  }

  private async loadSavedSieveSize(): Promise<void> {
    try {
      const savedSize = await this.storageService.get(SAVED_SIEVE_SIZE_KEY);
      if (savedSize) {
        const parsedSize = JSON.parse(savedSize) as SieveSize;
        const isValidSize = this.sieveSizeOptions.some(
          (option) => option.value === parsedSize
        );
        if (isValidSize) {
          this.savedSieveSize = parsedSize;
        }
      }
    } catch (error) {
      console.error('Error loading saved sieve size:', error);
    }
  }

  private async saveSieveSize(size: SieveSize): Promise<void> {
    try {
      await this.storageService.store(SAVED_SIEVE_SIZE_KEY, size);
      this.savedSieveSize = size;
    } catch (error) {
      console.error('Error saving sieve size:', error);
    }
  }

  private uploadPhoto(photo: WastePhoto): void {
    this.uploading = true;
    this.currentUploadProgress = 0;

    this.attachmentsService
      .saveFileFromDataUrlWithProgress(
        'WasteRegistration',
        this.orderId,
        photo.dataUrl,
        photo.fileName,
        photo.mimeType
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = Math.round((100 * event.loaded) / event.total);
            this.currentUploadProgress = progress;
            // Update progress directly on the photo object reference
            photo.uploadProgress = progress;
          }

          if (event.type === HttpEventType.Response) {
            const response = event.body;
            photo.uploading = false;
            photo.uploadProgress = 100;
            photo.id = response.id;
            photo.referenceId = response.referenceId;
            photo.fileUrl = AttachmentsService.getFileUrl(response.referenceId);

            if (this.photos.includes(photo)) {
              this.photosAddedList.push(photo);
            }
            this.uploading = false;
          }
        },
        error: (error) => {
          console.error('Error uploading photo:', error);
          // Remove the failed photo using the reference
          const photoIndex = this.photos.indexOf(photo);
          if (photoIndex !== -1) {
            this.photos.splice(photoIndex, 1);
          }
          this.uploading = false;
          this.snackbarService.open(
            'red',
            'WASTE_REGISTRATION.MESSAGES.ERROR_UPLOADING_PHOTO'
          );
        },
      });
  }

  private handleSourceChange(sourceId: WasteSource, checked: boolean): void {
    this.isUpdatingSource = true;
    try {
      if (checked) {
        // Uncheck all other sources
        this.wasteSourceOptions.forEach((option) => {
          if (option.id !== sourceId) {
            this.sourceControls[option.id].setValue(false, {
              emitEvent: false,
            });
          }
        });
        this.form.get('source')?.setValue(sourceId);

        // Reset sieve size when changing source
        this.isUpdatingSize = true;
        if (sourceId !== 'Sieve') {
          this.form.get('sieveSize')?.setValue(null);
        } else {
          this.form.get('sieveSize')?.setValue(this.savedSieveSize);
          this.sieveSizeOptions.forEach((size) => {
            this.sizeControls[size.id].setValue(
              size.value === this.savedSieveSize,
              {
                emitEvent: false,
              }
            );
          });
        }
        this.isUpdatingSize = false;
      } else {
        // Prevent unchecking the only selected option
        this.sourceControls[sourceId].setValue(true, { emitEvent: false });
      }
    } finally {
      this.isUpdatingSource = false;
    }
  }

  private handleSizeChange(
    sizeId: string,
    sizeValue: SieveSize,
    checked: boolean
  ): void {
    this.isUpdatingSize = true;
    try {
      if (checked) {
        // Uncheck all other sizes
        this.sieveSizeOptions.forEach((size) => {
          if (size.id !== sizeId) {
            this.sizeControls[size.id].setValue(false, { emitEvent: false });
          }
        });
        this.form.get('sieveSize')?.setValue(sizeValue);
      } else {
        // Prevent unchecking the only selected option
        this.sizeControls[sizeId].setValue(true, { emitEvent: false });
      }
    } finally {
      this.isUpdatingSize = false;
    }
  }

  private setupBackButton(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  private createForm(): FormGroup {
    return this.fb.group({
      source: ['Sieve' as WasteSource],
      sieveSize: ['1.18mm' as SieveSize],
      otherKg: [0],
      findings: [null],
    });
  }

  /**
   * Associates all added photos with the waste registration after it's saved
   * Similar to how action points handle photos in PC app
   */
  private async associatePhotosWithWaste(wasteId: number): Promise<void> {
    // Process added photos - associate them with the waste registration
    if (this.photosAddedList.length > 0) {
      const addPromises = this.photosAddedList.map((photo) =>
        this.attachmentsService
          .newAttachment({
            parentObject: 'WasteRegistration',
            parentID: wasteId.toString(),
            fileName: photo.fileName,
            description: photo.description || '',
            size: photo.size,
            type: 19, // Photo type
            mimeType: photo.mimeType,
            referenceId: photo.referenceId || '',
            customerShare: false,
            restricted: false,
          })
          .toPromise()
          .catch((error) => {
            console.error(`Error associating photo ${photo.fileName}:`, error);
            return null;
          })
      );

      await Promise.all(addPromises);
    }

    // Process deleted photos - remove them from the server
    if (this.photosDeletedList.length > 0) {
      const deletePromises = this.photosDeletedList.map((photo) =>
        this.attachmentsService
          .deletePhoto(photo.referenceId || '', photo.id || null)
          .toPromise()
          .catch((error) => {
            console.error(`Error deleting photo ${photo.fileName}:`, error);
            return null;
          })
      );

      await Promise.all(deletePromises);
    }
  }
}
